/** @type {Event | null} */
let deferredInstallPrompt = null;

/** @type {Set<() => void>} */
const installPromptListeners = new Set();

function notifyInstallPromptListeners() {
  for (const listener of installPromptListeners) {
    listener();
  }
}

/** @returns {Event | null} Chromium install prompt captured from `beforeinstallprompt`. */
export function getDeferredInstallPrompt() {
  return deferredInstallPrompt;
}

/**
 * Subscribe to deferred install prompt changes (capture, accept, or app installed).
 * @param {() => void} listener
 * @returns {() => void}
 */
export function subscribeDeferredInstallPrompt(listener) {
  installPromptListeners.add(listener);
  return () => installPromptListeners.delete(listener);
}

/**
 * Register `beforeinstallprompt` once at app startup so SPA navigations do not miss the event.
 */
export function ensurePwaInstallCapture() {
  if (typeof window === "undefined" || window.__horaizenPwaInstallCapture) {
    return;
  }

  window.__horaizenPwaInstallCapture = true;
  deferredInstallPrompt = null;

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredInstallPrompt = event;
    notifyInstallPromptListeners();
  });

  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    notifyInstallPromptListeners();
  });
}

/**
 * @returns {boolean} True when the app is running as an installed PWA.
 */
export function isPwaInstalled() {
  if (typeof window === "undefined") {
    return false;
  }

  const displayModes = ["standalone", "fullscreen", "minimal-ui"];
  const inDisplayMode = displayModes.some((mode) =>
    window.matchMedia(`(display-mode: ${mode})`).matches,
  );

  // ASSUMPTION: iOS Safari sets navigator.standalone when launched from home screen.
  const iosStandalone = Boolean(navigator.standalone);

  return inDisplayMode || iosStandalone;
}

/**
 * @returns {boolean} True on iPhone/iPad (including iPadOS desktop UA).
 */
export function isIosDevice() {
  if (typeof navigator === "undefined") {
    return false;
  }

  const ua = navigator.userAgent ?? "";
  if (/iphone|ipad|ipod/i.test(ua)) {
    return true;
  }

  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

/**
 * iOS in-app browsers (e.g. Instagram) cannot use Add to Home Screen from our UI.
 * @returns {boolean}
 */
export function isIosInAppBrowser() {
  if (!isIosDevice()) {
    return false;
  }

  const ua = navigator.userAgent ?? "";
  return /(CriOS|FxiOS|EdgiOS|GSA|Instagram|FBAN|FBAV|Line\/|Twitter)/i.test(ua);
}

/**
 * @returns {boolean} True when we can show manual Add to Home Screen steps on iOS.
 */
export function canShowIosInstallInstructions() {
  return isIosDevice() && !isIosInAppBrowser() && !isPwaInstalled();
}
